// Copyright (c) Ping. All rights reserved.
//
// Web host for the shared `createCallGroup` (per-call MLS group, calls.md
// §3–4). Wires the web's transport, KV adapter, claim-fanout, and identity —
// mirrors createWebGroupConversation but for ephemeral call infrastructure
// (no Dexie chat row, no hydration). The returned `callGroupIdHex` is the
// call_group_id the caller puts in the call_invite and derives the media key
// from.

import {
  addMembersToGroupConversation,
  type ClaimDevicesForAccount,
  createCallGroup,
  type CreateCallGroupResult,
  type GroupMemberInput,
  type MessagingClientLike,
} from '@ping/ui';

import { log } from '@core/log/log';
import { claimDevicesForGroupMember } from '@core/network/users';
import { retrieveIdentity } from '@core/persistence/identity';
import { kvAdapter } from '@features/chat/mlsConversation';
import { getCurrentTransport } from '@session/sessionBootstrap';

export interface CreateWebCallGroupParams {
  readonly client: MessagingClientLike;
  readonly members: readonly GroupMemberInput[];
  /** Lowercase-hex call id (group name = `call:<callId>`). */
  readonly callId: string;
  readonly signal?: AbortSignal;
}

export async function createWebCallGroup(
  params: CreateWebCallGroupParams,
): Promise<CreateCallGroupResult> {
  const identity = await retrieveIdentity();
  if (identity === null) {
    throw new Error('Still signing you in. Try again in a moment.');
  }
  return createCallGroup({
    client: params.client,
    transport: getCurrentTransport(),
    kv: kvAdapter,
    log,
    localAccountId: identity.accountId,
    members: params.members,
    callId: params.callId,
    claimDevicesForAccount: claimDevicesForGroupMember,
    ...(params.signal !== undefined ? { signal: params.signal } : {}),
  });
}

export interface AddWebCallGroupMembersParams {
  readonly client: MessagingClientLike;
  readonly callGroupIdHex: string;
  readonly existingPeerAccountIds: readonly string[];
  readonly newMembers: readonly GroupMemberInput[];
  /** Override device claiming — used by handoff to exclude the current device. */
  readonly claimDevicesForAccount?: ClaimDevicesForAccount;
}

/** Add participants to an existing per-call MLS group mid-call (invite flow). */
export async function addWebCallGroupMembers(params: AddWebCallGroupMembersParams): Promise<void> {
  const identity = await retrieveIdentity();
  if (identity === null) throw new Error('Not signed in.');
  await addMembersToGroupConversation({
    client: params.client,
    transport: getCurrentTransport(),
    conversationIdHex: params.callGroupIdHex,
    localAccountId: identity.accountId,
    localDisplayName: identity.displayName,
    existingPeerAccountIds: params.existingPeerAccountIds,
    newMembers: params.newMembers,
    claimDevicesForAccount: params.claimDevicesForAccount ?? claimDevicesForGroupMember,
    log,
  });
}
